import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { faUserAlt } from '@fortawesome/pro-solid-svg-icons/faUserAlt';
import { faUnlock } from '@fortawesome/pro-solid-svg-icons/faUnlock';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { linkIDs } from '../../../services/link-service/links';
import HelpArticleLink from '../../../components/help-article-link';

import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { getUserSettingsOptions } from '../../../services/settings/user-settings-service';
import { getSellerSettingOptions } from '../../../services/settings/seller-settings-service';
import { UserPermissionService, v2LoginDirectlyToRoutesToV3Routes } from '../../../services/user-permission-service';
import Global from '../../../store/global-selectors';

import style from './style.module.less';

const messages = defineMessages({
  enabled: {
    description: 'Display Options General Preferences Enabled',
    defaultMessage: 'Enabled',
    id: 'ZSigtE',
  },
  disabled: {
    description: 'Display Options General Preferences Disabled',
    defaultMessage: 'Disabled',
    id: 'VqiZNb',
  },
  learnMore: {
    description: 'Display Options General Preferences Learn More',
    defaultMessage: '(<a>Learn More</a>)',
    id: 'vJfBuS',
  },
});

const isQuickshipAllowedOptions = getSellerSettingOptions(SellerSettings.isQuickshipAllowed);
const getBoolSettingMessage = (isSettingEnabled) => {
  return isSettingEnabled ? messages.enabled : messages.disabled;
};

const urlToLoginOptionMap = getUserSettingsOptions(UserSettings.defaultUrl).reduce((hash, option) => {
  return {
    ...hash,
    [option.settingValue]: option,
  };
}, {});

const GeneralPreferencesTab = ({ defaultUrl, isQuickshipAllowed, setDefaultUrl, setIsQuickshipAllowed, userHasAdminOrConfigRole }) => {
  const userRoles = useSelector((state) => Global.selectUseruserRoles(state));
  const loginOptions = useMemo(
    () =>
      getUserSettingsOptions(UserSettings.defaultUrl).filter(({ settingValue }) => {
        return UserPermissionService.userIsInRoleOrHasAdministrationPermission(
          userRoles,
          v2LoginDirectlyToRoutesToV3Routes[settingValue].requiredPermissionRole,
        );
      }),
    [userRoles],
  );
  return (
    <div className={style['tab-content']}>
      <div className={classnames(style.section, style['non-header-buffer'])}>
        <div className={style.settings}>
          <label>
            <FontAwesomeIcon className={style['key-icon']} icon={faUsers} />
            <FormattedMessage {...SellerSettings.isQuickshipAllowed.messageDescriptor} />
          </label>
          <div className={style['setting-options']}>
            <RadioCheckList className={style['radio-checklist']}>
              {isQuickshipAllowedOptions.map((option) => (
                <RadioCheckListItem
                  key={option.value}
                  isChecked={option.value === isQuickshipAllowed}
                  isDisabled={!userHasAdminOrConfigRole}
                  onChecked={() => setIsQuickshipAllowed(option.value)}
                >
                  <FormattedMessage {...getBoolSettingMessage(option.value)} />
                </RadioCheckListItem>
              ))}
            </RadioCheckList>
            <span className={style['helper-text']}>
              <FormattedMessage
                {...messages.learnMore}
                values={{
                  a: (chunks) => <HelpArticleLink articleId={linkIDs.UsingQuickship}>{chunks}</HelpArticleLink>,
                }}
              />
            </span>
            <FontAwesomeIcon
              className={classnames(style.icon, style['permissions-icon'])}
              icon={userHasAdminOrConfigRole ? faUnlock : faLock}
            />
          </div>
          <label>
            <FontAwesomeIcon className={style['key-icon']} icon={faUserAlt} />
            <FormattedMessage {...UserSettings.defaultUrl.messageDescriptor} />
          </label>
          <div className={style['setting-options']}>
            <DropdownMenu variant='form'>
              <DropdownMenuToggler theme='white'>
                <FormattedMessage {...urlToLoginOptionMap[defaultUrl].messageDescriptor} />
              </DropdownMenuToggler>
              {loginOptions.map(({ settingValue, messageDescriptor }) => (
                <DropdownMenuItem key={settingValue} onClick={() => setDefaultUrl(settingValue)} selected={settingValue === defaultUrl}>
                  <FormattedMessage {...messageDescriptor} />
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

GeneralPreferencesTab.propTypes = {
  defaultUrl: PropTypes.string.isRequired,
  isQuickshipAllowed: PropTypes.bool.isRequired,
  setDefaultUrl: PropTypes.func.isRequired,
  setIsQuickshipAllowed: PropTypes.func.isRequired,
  userHasAdminOrConfigRole: PropTypes.bool.isRequired,
};

export default GeneralPreferencesTab;
